import { useEffect, useRef, useState } from 'react';
import sidebar_collapse_icon from '../../assets/images/sidebar_collapse_icon.svg';
import sidebar_open_icon from '../../assets/images/sidebar_open_icon.svg';
import ev_scanner_icon from '../../assets/images/ev_scanner_icon.svg';
import sportsbook_screener_icon from '../../assets/images/sportsbook_screener_icon.svg';
import discord_community_icon from '../../assets/images/discord_community_icon.svg';
import arbitrage_icon from '../../assets/images/arbitrage_icon2.svg';
import arbitrage_icon_selected from '../../assets/images/arbitrage_icon2-selected.svg';
import ev_scanner_icon_selected from '../../assets/images/ev_scanner_icon-selected.svg';
import outliers_icon from '../../assets/images/outliers_icon.svg';
import outliers_icon_selected from '../../assets/images/outliers_icon-selected.svg';
import learn_icon from '../../assets/images/learn_icon.svg';
import tools_icon from '../../assets/images/tools_icon.svg';

export const Sidebar = (props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [showText, setShowText] = useState(false);
	const [hoveredItem, setHoveredItem] = useState(null);
	const inputRef = useRef(`sidebar`);

	const location = window.location.pathname;

	useEffect(() => {
		function handleClickOutside(event) {
			if (inputRef.current && !inputRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		}

		function handleEscape(event) {
			if (event.key === 'Escape') {
				if (inputRef.current && !inputRef.current.contains(event.target)) {
					setIsOpen(false);
				}
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	// Delay text appearance until sidebar has expanded enough
	useEffect(() => {
		if (isOpen) {
			// Wait for sidebar width animation to progress before showing text
			const timer = setTimeout(() => {
				setShowText(true);
			}, 150); // Half of the 300ms transition
			return () => clearTimeout(timer);
		} else {
			// Hide text immediately when closing
			setShowText(false);
		}
	}, [isOpen]);

	const menuItems = [
		{ path: '/', label: 'EV Scanner', icon: ev_scanner_icon, selectedIcon: ev_scanner_icon_selected },
		{ path: '/arbitrage', label: 'Arbitrage', icon: arbitrage_icon, selectedIcon: arbitrage_icon_selected },
		{ path: '/outliers', label: 'Outliers', icon: outliers_icon, selectedIcon: outliers_icon_selected },
		{ path: '/screener', label: 'Sportsbook Screener', icon: sportsbook_screener_icon },
		{ path: 'https://t.co/Z41E3czDHW', label: 'Community', icon: discord_community_icon, isExternal: true },
		{ path: '/tools', label: 'Tools', icon: tools_icon },
		{ path: '/blog', label: 'Blog', icon: learn_icon },
	];

	const handleItemClick = (item) => {
		if (item.isExternal) {
			window.open(item.path);
		} else if (!item.comingSoon) {
			window.location.href = item.path;
		}
	};

	const sidebarTransition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

	return (
		<div
			ref={inputRef}
			style={{
				backgroundColor: '#171923',
				width: isOpen ? '250px' : '70px',
				minHeight: '109vh',
				borderRight: '1px solid rgba(255, 255, 255, 0.08)',
				transition: sidebarTransition,
				boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)',
				zIndex: 100,
				position: 'relative',
				overflow: 'hidden',
				flexShrink: 0,
			}}
			className="flex-col">
			<div className="py-4">
				{/* Toggle button - consistent position */}
				<div className="px-3 mb-4">
					<div
						onClick={() => setIsOpen(!isOpen)}
						className="flex items-center justify-between cursor-pointer hover:bg-gray-800 rounded-lg p-2"
						style={{ transition: sidebarTransition }}>
						{isOpen && (
							<div className="text-gray-300 font-medium" style={{ opacity: showText ? 1 : 0, transition: 'opacity 0.15s ease-in' }}>Menu</div>
						)}
						<img 
							src={isOpen ? sidebar_collapse_icon : sidebar_open_icon} 
							alt={isOpen ? "collapse sidebar icon" : "sidebar open icon"} 
							className="w-8 h-8 flex-shrink-0" 
						/>
					</div>
				</div>
				
				{/* Menu items - consistent structure */}
				<div className="px-3 space-y-2">
					{menuItems.map((item, index) => (
						<div
							key={index}
							onClick={() => handleItemClick(item)}
							onMouseEnter={() => !isOpen && setHoveredItem(item.label)}
							onMouseLeave={() => setHoveredItem(null)}
							style={{ transition: sidebarTransition }}
							className={`flex items-center p-2 rounded-lg cursor-pointer relative ${
								location === item.path
									? 'bg-gray-800 text-white'
									: 'text-gray-400 hover:bg-gray-800 hover:text-white'
							}`}>
							<img
								src={location === item.path ? item.selectedIcon || item.icon : item.icon}
								alt={`${item.label} icon`}
								className="w-8 h-8 flex-shrink-0"
								style={{ marginRight: isOpen ? '12px' : '0' }}
							/>
							{isOpen && (
								<div className="flex-1" style={{ opacity: showText ? 1 : 0, transition: 'opacity 0.15s ease-in' }}>
									<div className="whitespace-nowrap">{item.label}</div>
									{item.comingSoon && (
										<div style={{ 
											color: '#6b7280', 
											fontSize: '0.875rem', 
											fontWeight: 400,
											marginTop: '2px'
										}}>
											(coming soon)
										</div>
									)}
								</div>
							)}
							{!isOpen && hoveredItem === item.label && (
								<div
									className="absolute left-full ml-2 px-3 py-1 bg-gray-900 text-white text-base rounded-md whitespace-nowrap z-50"
									style={{ top: '50%', transform: 'translateY(-50%)' }}>
									{item.label}
									{item.comingSoon && ' (coming soon)'}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};
